import { useMemo } from "react";
import type { ChartConfig } from "../types";

// Helper function to safely access nested properties
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const get = (obj: any, path: string, defaultValue: any = undefined) => {
  const keys = path.split(".");
  let result = obj;
  for (const key of keys) {
    result = result?.[key];
    if (result === undefined || result === null) {
      return defaultValue;
    }
  }
  return result;
};

export interface ProcessedSeriesData {
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: { x: any; y: any }[];
}

export function useChartData<T = any>(
  rawData: T[] | undefined | null,
  config: Pick<ChartConfig, "xKey" | "series">
): {
  seriesData: ProcessedSeriesData[];
} {
  const seriesData = useMemo(() => {
    if (!rawData || !config || !config.xKey || !config.series) {
      return [];
    }

    return config.series.map((seriesConfig) => {
      const processedData = rawData
        .map((item) => {
          const xValue = get(item, config.xKey);
          const yValue = get(item, seriesConfig.key);

          // Only drop points with invalid x; preserve y=null so tooltip can show N/A
          if (xValue !== undefined && xValue !== null) {
            return {
              x: xValue,
              y: yValue !== undefined && yValue !== null ? yValue : null,
            };
          }
          return null;
        })
        .filter((item): item is { x: any; y: any } => item !== null); // Remove only entries without x

      return {
        key: seriesConfig.key,
        data: processedData,
      };
    });
  }, [rawData, config]);

  return { seriesData };
}
